import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthApiError } from '@supabase/supabase-js';
import { useAuth } from '@/lib/auth';
import { AuthShell } from './AuthShell';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { AlertTriangle } from 'lucide-react';

/**
 * Email + password sign-in. The auth hook (FND-4) bakes JWT claims so the
 * sidebar / RLS get clinic memberships immediately on the next render.
 */
export function SignIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionExpired] = useState(() => {
    try {
      const flag = sessionStorage.getItem('clinio_session_expired');
      if (flag) {
        sessionStorage.removeItem('clinio_session_expired');
        return true;
      }
    } catch {
      // sessionStorage not available
    }
    return false;
  });

  const from = (location.state as { from?: Location } | null)?.from?.pathname ?? '/dashboard';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.code === 'invalid_credentials' || err.status === 400) {
          setError(t('auth.errors.invalid_credentials'));
        } else if (err.status === 429) {
          setError(t('auth.errors.too_many_requests'));
        } else {
          setError(err.message || t('auth.errors.unknown'));
        }
      } else {
        setError(t('auth.errors.unknown'));
      }
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t('auth.sign_in.title')}
      subtitle={t('auth.sign_in.subtitle')}
      footer={
        <>
          {t('auth.sign_in.no_account')}{' '}
          <Link to="/create-clinic" className="font-medium text-indigo-600 hover:text-indigo-500">
            {t('auth.sign_in.create_account')}
          </Link>
        </>
      }
    >
      {sessionExpired && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('auth.sign_in.session_expired', 'Your session expired due to inactivity. Please sign in again.')}</span>
        </div>
      )}
      {error && <ErrorAlert message={error} />}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label={t('auth.sign_in.email')}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label={t('auth.sign_in.password')}
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <div className="flex items-center justify-end">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
          >
            {t('auth.sign_in.forgot_password')}
          </Link>
        </div>

        <Button type="submit" size="full" disabled={submitting}>
          {submitting ? t('auth.sign_in.submitting') : t('auth.sign_in.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
